import {
    Button,
    ButtonLinkStyle,
    checkAndSetError,
    clearInputFormErrorFields,
    convertInputFormToObject,
    InputForm,
    inputFormIsValid, NumberField, TextField,
    useInputForm
} from "../../../Components/Inputs";
import {GridCell, gridConstants} from "../../../Components/Grid";
import React from "react";
import {OrderProcessingForm} from "./OrderProcessingForm";
import {orderProcessingModel} from "./orderProcessingFactory";
import {orderProcessingFormVisibility} from "./OrderProcessingVisibilityFactory";
import {getButtonLabelFromOrderStatus} from "../orderUtility";
import {HtmlDisplay} from "../../../Components/Display";
import {OrderPrintBarcode} from "./OrderPrintBarcode";
import {ORDER_STATUS} from "../orderConstants";
import {locations} from "../../../Utilities/Location";
import {openInNewTab} from "../../../Utilities/htmlUtilities";
import {CheckInFormView} from "../../../Components/CheckIn/CheckInFormView";
import {LayoutMedium} from "../../../Components/Layout";

import type {HandleApiCall} from "../../../types";
import type {ListItem} from "../../../Utilities/Types/listUtilities";

interface OrderProcessingViewProps {
    handleApiCall: HandleApiCall;
    handleFulfillOrder: (data: Record<string, unknown>) => void;
    handlePlaceAnOrder: (data: Record<string, unknown>) => void;
    handleReceiveOrder: (data: Record<string, unknown>) => void;
    handleRevertFromComplete: () => void;
    handleReturn: () => void;
    orderDetails: Record<string, any>;
    redirect: (path: string) => void;
    setWarnings: (val: string[]) => void;
    vendors: ListItem[] | null;
}

export const OrderProcessingView = ({
                                        handleApiCall,
                                        handleFulfillOrder,
                                        handlePlaceAnOrder,
                                        handleReceiveOrder,
                                        handleRevertFromComplete,
                                        handleReturn,
                                        orderDetails,
                                        redirect,
                                        setWarnings,
                                        vendors,
                                    }: OrderProcessingViewProps) => {
    const formVisibility = orderProcessingFormVisibility({orderDetails});

    const model = orderProcessingModel({
        orderId: orderDetails.orderId,
        formatOrdered: orderDetails.format,
        orderType: orderDetails.orderType,
        isOrderComplete: orderDetails.isOrderComplete,
        description: orderDetails.description,
        poNumber: orderDetails.poNumber,
        cost: orderDetails.cost,
        orderStatus: orderDetails.orderStatus,
        vendorId: orderDetails.vendorId,
        vendor: orderDetails.vendor
    })
    const orderProcessingForm = useInputForm(model);

    const orderStatusLabel = getButtonLabelFromOrderStatus(orderDetails.orderStatus);

    const handleSubmitClick = () => {
        clearInputFormErrorFields(orderProcessingForm);

        if (formVisibility.canPlaceAnOrder) {
            checkAndSetError([
                orderProcessingForm.alternateFormat,
                orderProcessingForm.orderStatus,
            ]);
        }

        if (formVisibility.canReceiveOrder) {
            checkAndSetError([orderProcessingForm.numberOfVolumesReceived]);
        }

        if (inputFormIsValid(orderProcessingForm)) {
            const orderProcessingData = convertInputFormToObject(orderProcessingForm);
            if (formVisibility.canPlaceAnOrder) {
                handlePlaceAnOrder(orderProcessingData);
            } else if (formVisibility.canReceiveOrder) {
                handleReceiveOrder(orderProcessingData);
                orderProcessingForm.numberOfVolumesReceived.value = "";
                orderProcessingForm.volumesReceived.value = "";
            }
        }
    }

    const handlePackingList = () => {
        openInNewTab(locations.ORDER_PACKING_LIST.getLink(orderDetails.orderId).to);
    }

    const handleFulfillOrderClick = () => {
        if (confirm(`Are you sure you wanted to proceed with updating the order status?`)) {
            const fulfillOrderDetails = convertInputFormToObject(orderProcessingForm);
            handleFulfillOrder(fulfillOrderDetails);
        }
    }

    const handleRevertFromCompleteClick = () => {
        const revertStatus = orderDetails.studentId ? 'Ready To Mail' : 'Shelve Volumes';
        if (confirm(`Are you sure you want to revert this order to "${revertStatus}"? This will allow you to continue processing the order.`)) {
            handleRevertFromComplete();
        }
    }

    return <>
        {
            !formVisibility.isOrderComplete &&
            <>
                {

                    <InputForm name={`orderProcessingForm`}>
                        {
                            !formVisibility.canFulfillOrder &&
                            <OrderProcessingForm
                                orderProcessingForm={orderProcessingForm}
                                orderDetails={orderDetails}
                                vendors={vendors}
                                formVisibility={formVisibility}
                            />
                        }

                        <TextField
                            {...orderProcessingForm.poNumber}
                            label={`PO Number`}
                            medium_columns={gridConstants.column.SIX}/>

                        <NumberField
                            {...orderProcessingForm.cost}
                            isPositiveNumberOnly
                            medium_columns={gridConstants.column.SIX}/>

                    </InputForm>
                }
                {
                    formVisibility.displayBarcode &&
                    <OrderPrintBarcode
                        barcodes={orderDetails.volumes}
                        catalogCopyId={orderDetails.copyId}
                        redirect={redirect}
                    />
                }
                {
                    formVisibility.displayShelve &&
                    <LayoutMedium className={`margin--all`}>
                        <InputForm name={"CheckIn"}>
                            <CheckInFormView
                                defaultBarcode={orderDetails.accessNumber}
                                handleApiCall={handleApiCall}
                                orderId={orderDetails.orderId}
                                processName={`Shelve`}
                                setWarnings={setWarnings}
                            />
                        </InputForm>
                    </LayoutMedium>
                }
                <GridCell className={`text-center`}>
                    {
                        !formVisibility.canFulfillOrder &&
                        <Button
                            isCollapsed
                            isPrimary
                            label={'Save'}
                            name={`btnSubmit`}
                            onClick={handleSubmitClick}
                        />
                    }
                    {
                        orderDetails.orderStatus.toString() === ORDER_STATUS.PrintBarcode.id &&
                        <ButtonLinkStyle
                            hasSpacing
                            label={`Print Mailing Label`}
                            name={`btnPackingList`}
                            onClick={handlePackingList}
                        />
                    }
                    {
                        formVisibility.canFulfillOrder &&
                        <>
                            <p>{orderStatusLabel}?</p>

                            <Button
                                isCollapsed
                                isPrimary
                                label={`Yes`}
                                name={`btnFulfillOrder`}
                                onClick={handleFulfillOrderClick}
                            />
                        </>

                    }
                    <hr/>
                    <Button
                        isCollapsed
                        label={'Return to Order Management'}
                        name={`btnGoBack`}
                        onClick={handleReturn}
                    />
                </GridCell>
            </>
        }
        {
            formVisibility.isOrderComplete &&
            <GridCell className={`text-center`}>
                <HtmlDisplay html={`<br/><h3>Order successfully completed!</h3>`}/>
                <Button
                    isCollapsed
                    label={'Revert Order Status'}
                    name={`btnRevertFromComplete`}
                    onClick={handleRevertFromCompleteClick}
                />
                <br/><br/>
                <Button
                    isCollapsed
                    label={'Return to Order Management'}
                    name={`btnGoBack`}
                    onClick={handleReturn}
                />
            </GridCell>
        }
    </>;
};

